import * as React from 'react'
import { PLANT_PROCESS_QUERY_PARAM, plantHref } from '@atbash-labs/plantview-links'
import {
  usePlantSessionPaint,
  type PlantProcessTreeNode
} from '@axilon-apps/domains-ontology-ui-state'
import {
  PlantTreeNav,
  buildTreeByParentId,
  type ParentIdTreeRow,
  type PlantTreeNavGhost,
  type PlantTreeNavNode
} from './PlantTreeNav'
import { collectUnplacedGhosts } from './session-mark-ui'

/**
 * Processes tab adapter over {@link PlantTreeNav}. Maps the flat
 * `useGetPlantProcessTreeQuery` node list (parent via `HAS_SUBPROCESS`)
 * onto the shared tree rail: no kind icon, fully expanded by default so
 * the whole hierarchy is scannable. Selection is the `?process=<id>`
 * query param.
 *
 * When a session is painting, rows carry word-pill marks and deleted
 * processes render as ghost rows under their parent (or at the root when
 * unplaced). Implementation counts and parent rollup pills stay off the
 * rail — the expanded tree already shows each child mark in place.
 */
export interface ProcessTreeNavProps {
  nodes: PlantProcessTreeNode[]
  selectedId?: string
  isLoading?: boolean
  className?: string
}

export function ProcessTreeNav({
  nodes,
  selectedId,
  isLoading,
  className
}: ProcessTreeNavProps): React.ReactElement {
  const { active, filter, markFor, ghostsByContainerId, markById } = usePlantSessionPaint()
  const paintOn = active && filter.enabled

  const navNodes = React.useMemo(() => {
    const toNavNode = (row: ParentIdTreeRow<PlantProcessTreeNode>): PlantTreeNavNode => {
      const id = row.node.process.id
      const ghosts: PlantTreeNavGhost[] = []
      if (paintOn) {
        for (const ghost of ghostsByContainerId.get(id) ?? []) {
          if (!ghost.labels.includes('Process')) continue
          const view = markFor(ghost.id)
          if (view !== 'deleted' && view !== 'dimmed') continue
          ghosts.push({ id: ghost.id, name: ghost.name ?? ghost.id, view })
        }
      }
      return {
        id,
        href: plantHref(row.node.process.id, 'Process', row.node.process),
        label: row.node.process.name,
        mark: paintOn ? markFor(id) : undefined,
        ghosts: ghosts.length > 0 ? ghosts : undefined,
        groups: [{ nodes: row.children.map(toNavNode) }]
      }
    }
    return buildTreeByParentId(
      nodes,
      (node) => node.process.id,
      (node) => node.parentId
    ).map(toNavNode)
  }, [ghostsByContainerId, markFor, nodes, paintOn])

  const rootGhosts = React.useMemo((): PlantTreeNavGhost[] | undefined => {
    if (!paintOn) return undefined
    const liveIds = new Set(nodes.map((node) => node.process.id))
    const unplaced = collectUnplacedGhosts(markById, ghostsByContainerId, markFor, {
      label: 'Process',
      liveIds
    })
    if (unplaced.length === 0) return undefined
    return unplaced.map((ghost) => ({
      id: ghost.id,
      name: ghost.name ?? ghost.id,
      view: ghost.view
    }))
  }, [ghostsByContainerId, markById, markFor, nodes, paintOn])

  return (
    <PlantTreeNav
      heading="Process tree"
      nodes={navNodes}
      selectParam={PLANT_PROCESS_QUERY_PARAM}
      selectedId={selectedId}
      isLoading={isLoading}
      loadingText="Loading processes…"
      emptyTitle="No processes"
      emptyHint="Ingest documents to populate the process tree."
      defaultExpanded
      rootGhosts={rootGhosts}
      className={className}
    />
  )
}
